import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Add auth token interceptor
api.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken && accessToken !== 'null' && accessToken !== 'undefined') {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const apiService = {
  get: (url) => api.get(url).then(res => res.data),
  post: (url, data) => api.post(url, data).then(res => res.data),
  put: (url, data) => api.put(url, data).then(res => res.data),
  delete: (url) => api.delete(url).then(res => res.data),
};

const analyticsService = {
  /**
   * Get campaign statistics
   */
  async getCampaignStats(campaignId) {
    try {
      const response = await apiService.get(`/marketing/${campaignId}/stats`);
      return response;
    } catch (error) {
      console.error('Error fetching campaign stats:', error);
      throw error;
    }
  },

  /**
   * Get overview statistics for all campaigns
   */
  async getOverviewStats() {
    try {
      const response = await apiService.get('/marketing/stats/overview');
      return response;
    } catch (error) {
      console.error('Error fetching overview stats:', error);
      throw error;
    }
  },


  /**
   * Get VPS campaign statistics (from VPS-synced DynamoDB tables)
   */
  async getVpsStats({ startDate, endDate } = {}) {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const queryString = params.toString();
      const url = queryString ? `/marketing/vps/stats?${queryString}` : '/marketing/vps/stats';

      const response = await apiService.get(url);
      return response;
    } catch (error) {
      console.error('Error fetching VPS stats:', error);
      throw error;
    }
  },

  /**
   * Get campaign jobs
   */
  async getCampaignJobs(campaignId) {
    try {
      const response = await apiService.get(`/marketing/${campaignId}/jobs`);
      return response.jobs || [];
    } catch (error) {
      console.error('Error fetching campaign jobs:', error);
      throw error;
    }
  },

  /**
   * Get campaign events
   */
  async getCampaignEvents(campaignId, filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.type) params.append('type', filters.type);
      if (filters.recipientId) params.append('recipientId', filters.recipientId);
      if (filters.limit) params.append('limit', filters.limit);

      const queryString = params.toString();
      const url = queryString ? `/marketing/${campaignId}/events?${queryString}` : `/marketing/${campaignId}/events`;

      const response = await apiService.get(url);
      return response.events || [];
    } catch (error) {
      console.error('Error fetching campaign events:', error);
      throw error;
    }
  },

  /**
   * Get VPS campaign events (from VPS-synced DynamoDB tables)
   */
  async getVpsCampaignEvents(campaignId, filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.type) params.append('type', filters.type);
      if (filters.recipientId) params.append('recipientId', filters.recipientId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.limit) params.append('limit', filters.limit);

      const queryString = params.toString();
      const url = queryString ? `/marketing/vps/${campaignId}/events?${queryString}` : `/marketing/vps/${campaignId}/events`;

      const response = await apiService.get(url);
      return response.events || [];
    } catch (error) {
      console.error('Error fetching VPS campaign events:', error);
      throw error;
    }
  },

  /**
   * Get sending plan CSV for a VPS campaign
   */
  async getSendingPlan(campaignId) {
    try {
      const response = await apiService.get(`/marketing/vps/${campaignId}/sending-plan`);
      return response;
    } catch (error) {
      console.error('Error fetching sending plan:', error);
      throw error;
    }
  },

  /**
   * Update sending plan CSV for a VPS campaign
   */
  async updateSendingPlan(campaignId, csvData) {
    try {
      const response = await apiService.put(`/marketing/vps/${campaignId}/sending-plan`, { csvData });
      return response;
    } catch (error) {
      console.error('Error updating sending plan:', error);
      throw error;
    }
  },

  /**
   * Parse CSV data into structured format for display
   */
  parseSendingPlanCsv(csvData) {
    if (!csvData) return { headers: [], rows: [], senders: [] };

    const lines = csvData.trim().split('\n');
    if (lines.length === 0) return { headers: [], rows: [], senders: [] };

    // Parse header row
    const headers = lines[0].split(',');
    const senders = headers.slice(1, -1); // Skip 'date' and 'notes' columns

    // Parse data rows
    const rows = lines.slice(1).map(line => {
      const values = line.split(',');
      const date = values[0];
      const volumes = values.slice(1, -1).map(v => parseInt(v, 10) || 0);
      const notes = values[values.length - 1] || '';
      const total = volumes.reduce((sum, v) => sum + v, 0);

      return { date, volumes, notes, total };
    });

    return { headers, rows, senders };
  },

  /**
   * Convert structured data back to CSV string
   */
  formatSendingPlanCsv(headers, rows) {
    const headerLine = headers.join(',');
    const dataLines = rows.map(row => {
      return [row.date, ...row.volumes, row.notes].join(',');
    });
    return [headerLine, ...dataLines].join('\n');
  },

  /**
   * Get all warmup presets
   */
  async getPresets() {
    try {
      const response = await apiService.get('/marketing/presets');
      return response.presets || [];
    } catch (error) {
      console.error('Error fetching presets:', error);
      throw error;
    }
  },

  /**
   * Get preset details with schedule and triggers
   */
  async getPreset(presetId) {
    try {
      const response = await apiService.get(`/marketing/presets/${presetId}`);
      return response;
    } catch (error) {
      console.error('Error fetching preset:', error);
      throw error;
    }
  },

  /**
   * Apply preset to a VPS campaign
   */
  async applyPreset(campaignId, presetId, config) {
    try {
      const response = await apiService.post(`/marketing/vps/${campaignId}/apply-preset`, {
        presetId,
        ...config
      });
      return response;
    } catch (error) {
      console.error('Error applying preset:', error);
      throw error;
    }
  },

  /**
   * Get campaign triggers
   */
  async getCampaignTriggers(campaignId) {
    try {
      const response = await apiService.get(`/marketing/vps/${campaignId}/triggers`);
      return response;
    } catch (error) {
      // Don't log 404s - triggers endpoint may not exist yet
      if (error?.response?.status !== 404) {
        console.error('Error fetching campaign triggers:', error);
      }
      throw error;
    }
  },

  /**
   * Update campaign triggers
   */
  async updateCampaignTriggers(campaignId, triggers) {
    try {
      const response = await apiService.put(`/marketing/vps/${campaignId}/triggers`, { triggers });
      return response;
    } catch (error) {
      console.error('Error updating campaign triggers:', error);
      throw error;
    }
  },

  /**
   * Get campaign email template
   */
  async getCampaignTemplate(campaignId) {
    try {
      const response = await apiService.get(`/marketing/vps/${campaignId}/template`);
      return response;
    } catch (error) {
      console.error('Error fetching campaign template:', error);
      throw error;
    }
  },

  /**
   * Update campaign email template
   */
  async updateCampaignTemplate(campaignId, templateData) {
    try {
      const response = await apiService.put(`/marketing/vps/${campaignId}/template`, templateData);
      return response;
    } catch (error) {
      console.error('Error updating campaign template:', error);
      throw error;
    }
  },

  /**
   * Get historical sending stats (aggregated sent events by date and sender)
   */
  async getHistoricalSendingStats(campaignId, startDate, endDate) {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const queryString = params.toString();
      const url = queryString
        ? `/marketing/vps/${campaignId}/history?${queryString}`
        : `/marketing/vps/${campaignId}/history`;
      const response = await apiService.get(url);
      return response;
    } catch (error) {
      console.error('Error fetching historical sending stats:', error);
      throw error;
    }
  }
};

export default analyticsService;

